/**
 * The rules an equation row must pass against the graph it is in, and which
 * nodes can come before an edge.
 */
import { RELATIONSHIP_ACCESS_LEVELS } from '../crm/relationship.js';
import { FIELD_VALUE_TYPES, NEGATIVE_OPERATORS, OPERATORS_BY_VALUE_TYPE, PRESENCE_OPERATORS, variableName, } from './equation.js';
import { GRAPH_EVENT_REGISTRY, isEntryEdge, } from './graph.js';
/** Raised by something attached to the graph, so it counts from anywhere. */
export function isGraphEvent(event) {
    return GRAPH_EVENT_REGISTRY.get(event)?.origin === 'scratchpad';
}
/** How a form answer type compares. */
export function valueTypeOfAnswer(answerType) {
    switch (answerType.kind) {
        case 'yes_no':
        case 'options':
            return 'choice';
        case 'integer':
            return 'integer';
        case 'date':
            return 'date';
        default:
            return 'text';
    }
}
/** The fixed choices of a choice value; empty for other types. */
export function choicesOfAnswer(answerType) {
    if (answerType.kind === 'yes_no')
        return ['yes', 'no'];
    if (answerType.kind === 'options')
        return answerType.options;
    return [];
}
/** The answer type of a node's question or signal, if the node has it. */
function answerTypeOf(source, node, item) {
    if (source === 'question' && node?.type === 'form') {
        const found = node.items.find(i => i.id === item);
        return found && found.kind === 'question' ? found.answerType : null;
    }
    // A signal reads like a yes / no answer.
    if (source === 'signal' && node?.type === 'conversation') {
        return node.signals?.some(s => s.id === item) ? { kind: 'yes_no' } : null;
    }
    return null;
}
/** What is wrong with a value or result row on this edge. */
export function rowIssues(row, edge, context) {
    if (row.kind === 'result')
        return resultIssues(row, edge, context);
    const ref = row.ref;
    const label = variableName(ref);
    let type;
    let choices = [];
    if (ref.source === 'system' || ref.source === 'contact') {
        type = FIELD_VALUE_TYPES[ref.field];
        if (ref.field === 'group')
            choices = RELATIONSHIP_ACCESS_LEVELS;
    }
    else {
        const node = context.nodeById.get(ref.nodeId);
        const answerType = answerTypeOf(ref.source, node, ref.item);
        if (!answerType) {
            return [
                {
                    code: 'unknown_reference',
                    message: `${missing(ref.source, ref.item, node)}`,
                },
            ];
        }
        if (!context.before().has(ref.nodeId)) {
            return [
                {
                    code: 'unreachable_reference',
                    message: `${label} on "${node?.title}" cannot exist here: the call never passes that node before this edge`,
                },
            ];
        }
        type = ref.source === 'signal' ? 'boolean' : valueTypeOfAnswer(answerType);
        choices = ref.source === 'signal' ? [] : choicesOfAnswer(answerType);
        // A negative check on the node's own question counts once the form has
        // finished. The node's own signal has no such moment: it is missing until
        // the node hears it, so a negative check would hold on entry.
        if (ref.source === 'signal' &&
            ref.nodeId === edge.fromNodeId &&
            NEGATIVE_OPERATORS.includes(row.op)) {
            return [
                {
                    code: 'negative_on_own_output',
                    message: `${label} is missing until this node hears it, so "${row.op}" would leave first; check its value instead`,
                },
            ];
        }
    }
    // A system variable always has a value, so it takes equals only.
    const operators = ref.source === 'system' ? ['equals'] : OPERATORS_BY_VALUE_TYPE[type];
    if (!operators.includes(row.op)) {
        return [
            {
                code: 'operator_type_mismatch',
                message: `${label} cannot be compared with "${row.op}"`,
            },
        ];
    }
    const problem = valueProblem(row, type, choices);
    return problem
        ? [{ code: 'value_type_mismatch', message: `${label}: ${problem}` }]
        : [];
}
/** An output names the node the edge leaves; earlier nodes are read as values. */
function resultIssues(row, edge, context) {
    const node = context.nodeById.get(row.nodeId);
    if (row.source === 'action') {
        if (node?.type !== 'action') {
            return [
                {
                    code: 'unknown_reference',
                    message: missing('action', undefined, node),
                },
            ];
        }
    }
    else if (row.source === 'form') {
        if (node?.type !== 'form') {
            return [
                {
                    code: 'unknown_reference',
                    message: missing('form', undefined, node),
                },
            ];
        }
    }
    else if (!answerTypeOf(row.source, node, row.item)) {
        return [
            {
                code: 'unknown_reference',
                message: missing(row.source, row.item, node),
            },
        ];
    }
    if (row.nodeId !== edge.fromNodeId) {
        return [
            {
                code: 'output_of_another_node',
                message: `"${node?.title}" is not the node this edge leaves; only that node reports how it went. Read its value instead`,
            },
        ];
    }
    // With no required question the check never holds.
    if (row.source === 'form' &&
        row.is === 'required_complete' &&
        node?.type === 'form' &&
        !node.items.some(i => i.kind === 'question' && i.required)) {
        return [
            {
                code: 'nothing_required',
                message: `"${node.title}" has no required question, so "required questions complete" holds at once`,
            },
        ];
    }
    return [];
}
function valueProblem(row, type, choices) {
    if (PRESENCE_OPERATORS.includes(row.op)) {
        return row.value === undefined ? null : `"${row.op}" takes no value`;
    }
    if (row.value === undefined)
        return `"${row.op}" needs a value`;
    if (type === 'boolean') {
        return typeof row.value === 'boolean'
            ? null
            : 'the value must be true or false';
    }
    if (type === 'integer') {
        return typeof row.value === 'number' && Number.isInteger(row.value)
            ? null
            : 'the value must be a whole number';
    }
    if (typeof row.value !== 'string' || row.value.trim() === '') {
        return 'the value must be text';
    }
    if (type === 'choice' && !choices.includes(row.value)) {
        return `"${row.value}" is not one of ${choices.join(', ')}`;
    }
    if (type === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(row.value)) {
        return 'the value must be a date written YYYY-MM-DD';
    }
    return null;
}
function missing(source, item, node) {
    if (!node)
        return `${source} "${item ?? ''}" names a node the graph does not have`;
    if (source === 'form')
        return `"${node.title}" is not a form`;
    if (source === 'action')
        return `"${node.title}" is not an action node`;
    return `"${node.title}" has no ${source} "${item}"`;
}
/**
 * Where a node's outputs can exist when an edge is checked: `every` when the
 * call passes through the node on every way to the edge, `some` when only on
 * some. A node missing from the map is never passed before the edge.
 *
 * The call starts at the entry edge's target. A cross-cutting edge can fire
 * from any node, so it counts as an edge into its target from every node. An
 * edge from anywhere can be checked after any node, so every node is `some`.
 * The node an edge leaves is `every`.
 */
export function nodesBefore(graph, edge) {
    const result = new Map();
    if (edge.fromNodeId == null) {
        for (const node of graph.nodes)
            result.set(node.id, 'some');
        return result;
    }
    const target = edge.fromNodeId;
    const { out, into, start } = moves(graph);
    const ancestors = walk(target, into);
    for (const id of ancestors)
        result.set(id, 'some');
    if (start) {
        for (const id of dominatorsOf(target, start, out, into))
            result.set(id, 'every');
    }
    result.set(target, 'every');
    return result;
}
/** Every move the call can make between nodes, both ways, and where it starts. */
function moves(graph) {
    const out = new Map();
    const into = new Map();
    const link = (from, to) => {
        if (!out.has(from))
            out.set(from, new Set());
        if (!into.has(to))
            into.set(to, new Set());
        out.get(from).add(to);
        into.get(to).add(from);
    };
    let start;
    for (const e of graph.edges) {
        if (e.toNodeId == null)
            continue;
        if (e.fromNodeId != null) {
            link(e.fromNodeId, e.toNodeId);
        }
        else if (isEntryEdge(e)) {
            start = e.toNodeId;
        }
        else {
            for (const node of graph.nodes) {
                if (node.id !== e.toNodeId)
                    link(node.id, e.toNodeId);
            }
        }
    }
    return { out, into, start };
}
function walk(from, next) {
    const seen = new Set([from]);
    const stack = [from];
    while (stack.length > 0) {
        for (const id of next.get(stack.pop()) ?? []) {
            if (!seen.has(id)) {
                seen.add(id);
                stack.push(id);
            }
        }
    }
    return seen;
}
/**
 * The nodes on every path from `start` to `target`, by iterative dataflow.
 * Graphs are small enough that plain sets do.
 */
function dominatorsOf(target, start, out, into) {
    const reachable = walk(start, out);
    if (!reachable.has(target))
        return new Set();
    const dom = new Map();
    for (const id of reachable) {
        dom.set(id, id === start ? new Set([start]) : new Set(reachable));
    }
    let changed = true;
    while (changed) {
        changed = false;
        for (const id of reachable) {
            if (id === start)
                continue;
            const sets = [...(into.get(id) ?? [])]
                .filter(p => reachable.has(p))
                .map(p => dom.get(p));
            const next = new Set(sets.length === 0
                ? []
                : [...sets[0]].filter(x => sets.every(s => s.has(x))));
            next.add(id);
            // A set only ever shrinks, so an unchanged size means unchanged.
            if (next.size !== dom.get(id).size) {
                dom.set(id, next);
                changed = true;
            }
        }
    }
    return dom.get(target);
}
//# sourceMappingURL=equation-rules.js.map